import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../lib/AuthContext';
import { apiClient } from '../lib/api';
import { toast } from 'sonner';
import {
    Users, Shield, Plus, Pencil, Trash2, Key, ToggleLeft, ToggleRight,
    Save, X, UserCircle, Crown, User as UserIcon, ChevronDown,
} from 'lucide-react';

const ROLE_BADGES = {
    super_admin: { label: 'Super Admin', color: 'text-purple-700', icon: Crown },
    admin: { label: 'Admin', color: 'text-blue-700', icon: Shield },
    user: { label: 'User', color: 'text-slate-600', icon: UserIcon },
};

function RoleBadge({ role }) {
    const badge = ROLE_BADGES[role] || ROLE_BADGES.user;
    const Icon = badge.icon;
    return (
        <span className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-xl text-xs font-bold ${badge.color}`} style={{
            background: 'var(--neu-bg)',
            boxShadow: 'inset 2px 2px 4px rgba(163,177,198,0.35), inset -2px -2px 4px rgba(255,255,255,0.5)',
        }}>
            <Icon className="w-3 h-3" />{badge.label}
        </span>
    );
}

// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
//  User Modal
// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
function UserModal({ isOpen, onClose, onSave, editUser, currentUserRole }) {
    const [form, setForm] = useState({ username: '', password: '', email: '', full_name: '', role: 'user' });
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        if (editUser) {
            setForm({ username: editUser.username, password: '', email: editUser.email || '', full_name: editUser.full_name || '', role: editUser.role });
        } else {
            setForm({ username: '', password: '', email: '', full_name: '', role: 'user' });
        }
    }, [editUser, isOpen]);

    if (!isOpen) return null;

    const handleSubmit = async (e) => {
        e.preventDefault();
        setSaving(true);
        try {
            await onSave(form, editUser);
            onClose();
        } catch (err) {
            toast.error(err.message);
        }
        setSaving(false);
    };

    return (
        <div className="fixed inset-0 flex items-center justify-center z-50" style={{
            background: 'rgba(224,229,236,0.75)',
            backdropFilter: 'blur(8px)',
        }}>
            <div className="max-w-md w-full mx-4 p-7 rounded-3xl" style={{
                background: 'var(--neu-bg)',
                boxShadow: '10px 10px 20px 0 rgba(163,177,198,0.7), -10px -10px 20px 0 rgba(255,255,255,0.85)',
                animation: 'neuPop 0.3s cubic-bezier(0.34,1.56,0.64,1)',
            }}>
                <div className="flex items-center justify-between mb-4">
                    <h3 className="text-lg font-bold text-slate-900">{editUser ? 'Edit User' : 'Create User'}</h3>
                    <button onClick={onClose} className="p-1 rounded-lg hover:bg-slate-100"><X className="w-5 h-5" /></button>
                </div>
                <form onSubmit={handleSubmit} className="space-y-4">
                    <div>
                        <label className="block text-sm font-medium text-slate-700 mb-1">Username</label>
                        <input
                            value={form.username}
                            onChange={(e) => setForm({ ...form, username: e.target.value })}
                            disabled={!!editUser}
                            required
                            className="w-full px-3 py-2 rounded-xl border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 disabled:bg-slate-50 disabled:text-slate-500"
                        />
                    </div>
                    {!editUser && (
                        <div>
                            <label className="block text-sm font-medium text-slate-700 mb-1">Password</label>
                            <input
                                type="password"
                                value={form.password}
                                onChange={(e) => setForm({ ...form, password: e.target.value })}
                                required={!editUser}
                                minLength={4}
                                className="w-full px-3 py-2 rounded-xl border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
                            />
                        </div>
                    )}
                    <div>
                        <label className="block text-sm font-medium text-slate-700 mb-1">Full Name</label>
                        <input
                            value={form.full_name}
                            onChange={(e) => setForm({ ...form, full_name: e.target.value })}
                            className="w-full px-3 py-2 rounded-xl border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
                        />
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-slate-700 mb-1">Email</label>
                        <input
                            type="email"
                            value={form.email}
                            onChange={(e) => setForm({ ...form, email: e.target.value })}
                            className="w-full px-3 py-2 rounded-xl border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
                        />
                    </div>
                    {currentUserRole === 'super_admin' && (
                        <div>
                            <label className="block text-sm font-medium text-slate-700 mb-1">Role</label>
                            <select
                                value={form.role}
                                onChange={(e) => setForm({ ...form, role: e.target.value })}
                                className="w-full px-3 py-2 rounded-xl border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
                            >
                                <option value="user">User</option>
                                <option value="admin">Admin</option>
                                <option value="super_admin">Super Admin</option>
                            </select>
                        </div>
                    )}
                    <div className="flex gap-3 pt-2">
                        <button type="button" onClick={onClose} className="flex-1 btn-secondary">Cancel</button>
                        <button
                            type="submit"
                            disabled={saving}
                            className="flex-1 px-4 py-2 bg-primary-600 hover:bg-primary-700 text-white rounded-xl font-semibold transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
                        >
                            {saving ? <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" /> : <Save className="w-4 h-4" />}
                            {editUser ? 'Update' : 'Create'}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
}

// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
//  Password Reset Modal
// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
function ResetPasswordModal({ isOpen, onClose, targetUser }) {
    const [newPassword, setNewPassword] = useState('');
    const [saving, setSaving] = useState(false);

    if (!isOpen || !targetUser) return null;

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (newPassword.length < 4) { toast.error('Password must be at least 4 characters'); return; }
        setSaving(true);
        try {
            await apiClient.resetUserPassword(targetUser.id, newPassword);
            toast.success(`Password reset for ${targetUser.username}`);
            onClose();
            setNewPassword('');
        } catch (err) {
            toast.error(err.message);
        }
        setSaving(false);
    };

    return (
        <div className="fixed inset-0 flex items-center justify-center z-50" style={{
            background: 'rgba(224,229,236,0.75)',
            backdropFilter: 'blur(8px)',
        }}>
            <div className="max-w-sm w-full mx-4 p-7 rounded-3xl" style={{
                background: 'var(--neu-bg)',
                boxShadow: '10px 10px 20px 0 rgba(163,177,198,0.7), -10px -10px 20px 0 rgba(255,255,255,0.85)',
                animation: 'neuPop 0.3s cubic-bezier(0.34,1.56,0.64,1)',
            }}>
                <h3 className="text-lg font-bold text-slate-900 mb-1">Reset Password</h3>
                <p className="text-sm text-slate-500 mb-4">Set new password for <strong>{targetUser.username}</strong></p>
                <form onSubmit={handleSubmit} className="space-y-4">
                    <input
                        type="password"
                        value={newPassword}
                        onChange={(e) => setNewPassword(e.target.value)}
                        placeholder="New password (min 4 chars)"
                        minLength={4}
                        required
                        className="w-full px-3 py-2 rounded-xl border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
                    />
                    <div className="flex gap-3">
                        <button type="button" onClick={onClose} className="flex-1 btn-secondary">Cancel</button>
                        <button type="submit" disabled={saving} className="flex-1 px-4 py-2 bg-amber-500 hover:bg-amber-600 text-white rounded-xl font-semibold transition-colors disabled:opacity-50 flex items-center justify-center gap-2">
                            <Key className="w-4 h-4" />{saving ? 'Resetting...' : 'Reset'}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
}

// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
//  Users Tab
// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
function UsersTab({ currentUser }) {
    const [users, setUsers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showModal, setShowModal] = useState(false);
    const [editUser, setEditUser] = useState(null);
    const [resetTarget, setResetTarget] = useState(null);

    const fetchUsers = useCallback(async () => {
        try {
            const data = await apiClient.getUsers();
            setUsers(data);
        } catch (err) {
            toast.error('Failed to load users');
        }
        setLoading(false);
    }, []);

    useEffect(() => { fetchUsers(); }, [fetchUsers]);

    const handleSave = async (form, existingUser) => {
        if (existingUser) {
            await apiClient.updateUserAccount(existingUser.id, {
                email: form.email, full_name: form.full_name, role: form.role,
            });
            toast.success(`User "${form.username}" updated`);
        } else {
            await apiClient.createUserAccount(form);
            toast.success(`User "${form.username}" created`);
        }
        fetchUsers();
    };

    const handleToggleActive = async (user) => {
        try {
            await apiClient.updateUserAccount(user.id, { is_active: !user.is_active });
            toast.success(`User ${user.is_active ? 'deactivated' : 'activated'}`);
            fetchUsers();
        } catch (err) {
            toast.error(err.message);
        }
    };

    const handleDelete = async (user) => {
        if (!confirm(`Delete user "${user.username}"?`)) return;
        try {
            await apiClient.deleteUserAccount(user.id);
            toast.success(`User "${user.username}" deleted`);
            fetchUsers();
        } catch (err) {
            toast.error(err.message);
        }
    };

    if (loading) return <div className="flex justify-center py-12"><div className="w-8 h-8 border-4 border-primary-200 border-t-primary-600 rounded-full animate-spin" /></div>;

    return (
        <div>
            <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-bold text-slate-900">Users ({users.length})</h3>
                <button onClick={() => { setEditUser(null); setShowModal(true); }} className="inline-flex items-center gap-2 px-4 py-2 bg-primary-600 hover:bg-primary-700 text-white rounded-xl font-semibold text-sm transition-colors">
                    <Plus className="w-4 h-4" />Add User
                </button>
            </div>

            <div className="overflow-x-auto rounded-xl border border-slate-200">
                <table className="w-full text-sm">
                    <thead>
                        <tr className="bg-slate-50 border-b border-slate-200">
                            <th className="px-4 py-3 text-left font-semibold text-slate-600">User</th>
                            <th className="px-4 py-3 text-left font-semibold text-slate-600">Role</th>
                            <th className="px-4 py-3 text-left font-semibold text-slate-600">Status</th>
                            <th className="px-4 py-3 text-left font-semibold text-slate-600">Last Login</th>
                            <th className="px-4 py-3 text-right font-semibold text-slate-600">Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                        {users.map((u) => (
                            <tr key={u.id} className="border-b border-slate-100 hover:bg-slate-50/50 transition-colors">
                                <td className="px-4 py-3">
                                    <div className="flex items-center gap-3">
                                        <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-primary-100 to-cyan-50 flex items-center justify-center">
                                            <UserCircle className="w-5 h-5 text-primary-600" />
                                        </div>
                                        <div>
                                            <div className="font-semibold text-slate-900">{u.full_name || u.username}</div>
                                            <div className="text-xs text-slate-500">{u.username}{u.email ? ` • ${u.email}` : ''}</div>
                                        </div>
                                    </div>
                                </td>
                                <td className="px-4 py-3"><RoleBadge role={u.role} /></td>
                                <td className="px-4 py-3">
                                    <button onClick={() => handleToggleActive(u)} disabled={u.id === currentUser?.id}
                                        className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-xs font-semibold border transition-colors ${u.is_active
                                            ? 'bg-emerald-50 text-emerald-700 border-emerald-200 hover:bg-emerald-100'
                                            : 'bg-red-50 text-red-600 border-red-200 hover:bg-red-100'
                                        } disabled:opacity-50 disabled:cursor-not-allowed`}
                                    >
                                        {u.is_active ? <ToggleRight className="w-4 h-4" /> : <ToggleLeft className="w-4 h-4" />}
                                        {u.is_active ? 'Active' : 'Inactive'}
                                    </button>
                                </td>
                                <td className="px-4 py-3 text-slate-500 text-xs">
                                    {u.last_login ? new Date(u.last_login).toLocaleString() : 'Never'}
                                </td>
                                <td className="px-4 py-3">
                                    <div className="flex items-center justify-end gap-1">
                                        <button onClick={() => { setEditUser(u); setShowModal(true); }} className="p-2 rounded-lg hover:bg-slate-100 text-slate-500 hover:text-primary-600" title="Edit">
                                            <Pencil className="w-4 h-4" />
                                        </button>
                                        <button onClick={() => setResetTarget(u)} className="p-2 rounded-lg hover:bg-slate-100 text-slate-500 hover:text-amber-600" title="Reset Password">
                                            <Key className="w-4 h-4" />
                                        </button>
                                        {u.id !== currentUser?.id && (
                                            <button onClick={() => handleDelete(u)} className="p-2 rounded-lg hover:bg-red-50 text-slate-500 hover:text-red-600" title="Delete">
                                                <Trash2 className="w-4 h-4" />
                                            </button>
                                        )}
                                    </div>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            <UserModal isOpen={showModal} onClose={() => setShowModal(false)} onSave={handleSave} editUser={editUser} currentUserRole={currentUser?.role} />
            <ResetPasswordModal isOpen={!!resetTarget} onClose={() => setResetTarget(null)} targetUser={resetTarget} />
        </div>
    );
}

// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
//  Permissions Tab
// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
function PermissionsTab() {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [changes, setChanges] = useState({});

    useEffect(() => {
        apiClient.getPermissions().then(d => { setData(d); setLoading(false); }).catch(() => setLoading(false));
    }, []);

    if (loading || !data) return <div className="flex justify-center py-12"><div className="w-8 h-8 border-4 border-primary-200 border-t-primary-600 rounded-full animate-spin" /></div>;

    const togglePermission = (role, menuKey, field) => {
        if (role === 'super_admin') return;
        const key = `${role}_${menuKey}_${field}`;
        const currentPerms = data.permissions[role];
        const perm = currentPerms.find(p => p.menu_key === menuKey);
        const currentVal = changes[key] !== undefined ? changes[key] : (perm ? perm[field] : false);
        setChanges({ ...changes, [key]: !currentVal });
    };

    const getPermValue = (role, menuKey, field) => {
        const key = `${role}_${menuKey}_${field}`;
        if (changes[key] !== undefined) return changes[key];
        const perm = data.permissions[role]?.find(p => p.menu_key === menuKey);
        return perm ? perm[field] : false;
    };

    const handleSave = async () => {
        setSaving(true);
        try {
            for (const role of ['admin', 'user']) {
                const perms = data.menu_items.map(m => ({
                    menu_key: m.key,
                    can_access: getPermValue(role, m.key, 'can_access'),
                    can_edit: getPermValue(role, m.key, 'can_edit'),
                }));
                await apiClient.updatePermissionsForRole(role, perms);
            }
            toast.success('Permissions saved');
            setChanges({});
            const refreshed = await apiClient.getPermissions();
            setData(refreshed);
        } catch (err) {
            toast.error(err.message);
        }
        setSaving(false);
    };

    const hasChanges = Object.keys(changes).length > 0;

    return (
        <div>
            <div className="flex items-center justify-between mb-4">
                <div>
                    <h3 className="text-lg font-bold text-slate-900">Role Permissions</h3>
                    <p className="text-sm text-slate-500">Control which menus each role can access</p>
                </div>
                {hasChanges && (
                    <button onClick={handleSave} disabled={saving}
                        className="inline-flex items-center gap-2 px-4 py-2 bg-emerald-600 hover:bg-emerald-700 text-white rounded-xl font-semibold text-sm transition-colors disabled:opacity-50">
                        {saving ? <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" /> : <Save className="w-4 h-4" />}
                        Save Changes
                    </button>
                )}
            </div>

            <div className="overflow-x-auto rounded-xl border border-slate-200">
                <table className="w-full text-sm">
                    <thead>
                        <tr className="bg-slate-50 border-b border-slate-200">
                            <th className="px-4 py-3 text-left font-semibold text-slate-600">Menu</th>
                            {data.roles.map(r => (
                                <th key={r} className="px-4 py-3 text-center font-semibold text-slate-600" colSpan={2}>
                                    <RoleBadge role={r} />
                                </th>
                            ))}
                        </tr>
                        <tr className="bg-slate-50/50 border-b border-slate-200">
                            <th className="px-4 py-2"></th>
                            {data.roles.map(r => (
                                <React.Fragment key={r}>
                                    <th className="px-2 py-2 text-center text-xs text-slate-500 font-medium">Access</th>
                                    <th className="px-2 py-2 text-center text-xs text-slate-500 font-medium">Edit</th>
                                </React.Fragment>
                            ))}
                        </tr>
                    </thead>
                    <tbody>
                        {data.menu_items.map(m => (
                            <tr key={m.key} className="border-b border-slate-100 hover:bg-slate-50/50">
                                <td className="px-4 py-3 font-medium text-slate-900">{m.label}</td>
                                {data.roles.map(r => (
                                    <React.Fragment key={r}>
                                        <td className="px-2 py-3 text-center">
                                            <button
                                                onClick={() => togglePermission(r, m.key, 'can_access')}
                                                disabled={r === 'super_admin'}
                                                className="disabled:opacity-50 disabled:cursor-not-allowed"
                                            >
                                                <div className={`w-9 h-5 rounded-full flex items-center transition-colors ${getPermValue(r, m.key, 'can_access') ? 'bg-emerald-500 justify-end' : 'bg-slate-300 justify-start'}`}>
                                                    <div className="w-4 h-4 bg-white rounded-full mx-0.5 shadow-sm" />
                                                </div>
                                            </button>
                                        </td>
                                        <td className="px-2 py-3 text-center">
                                            <button
                                                onClick={() => togglePermission(r, m.key, 'can_edit')}
                                                disabled={r === 'super_admin'}
                                                className="disabled:opacity-50 disabled:cursor-not-allowed"
                                            >
                                                <div className={`w-9 h-5 rounded-full flex items-center transition-colors ${getPermValue(r, m.key, 'can_edit') ? 'bg-blue-500 justify-end' : 'bg-slate-300 justify-start'}`}>
                                                    <div className="w-4 h-4 bg-white rounded-full mx-0.5 shadow-sm" />
                                                </div>
                                            </button>
                                        </td>
                                    </React.Fragment>
                                ))}
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </div>
    );
}

// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
//  Change Password Tab
// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
function ChangePasswordTab() {
    const [oldPassword, setOldPassword] = useState('');
    const [newPassword, setNewPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [saving, setSaving] = useState(false);

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (newPassword !== confirmPassword) { toast.error('Passwords do not match'); return; }
        if (newPassword.length < 4) { toast.error('Password must be at least 4 characters'); return; }
        setSaving(true);
        try {
            await apiClient.authChangePassword(oldPassword, newPassword);
            toast.success('Password changed successfully');
            setOldPassword(''); setNewPassword(''); setConfirmPassword('');
        } catch (err) {
            toast.error(err.message);
        }
        setSaving(false);
    };

    return (
        <div className="max-w-md">
            <h3 className="text-lg font-bold text-slate-900 mb-4">Change Your Password</h3>
            <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">Current Password</label>
                    <input type="password" value={oldPassword} onChange={(e) => setOldPassword(e.target.value)} required
                        className="w-full px-3 py-2 rounded-xl border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500" />
                </div>
                <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">New Password</label>
                    <input type="password" value={newPassword} onChange={(e) => setNewPassword(e.target.value)} required minLength={4}
                        className="w-full px-3 py-2 rounded-xl border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500" />
                </div>
                <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">Confirm New Password</label>
                    <input type="password" value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)} required minLength={4}
                        className="w-full px-3 py-2 rounded-xl border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500" />
                </div>
                <button type="submit" disabled={saving}
                    className="px-6 py-2 bg-primary-600 hover:bg-primary-700 text-white rounded-xl font-semibold text-sm transition-colors disabled:opacity-50 flex items-center gap-2">
                    {saving ? <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" /> : <Key className="w-4 h-4" />}
                    Change Password
                </button>
            </form>
        </div>
    );
}

// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
//  Main Admin Page
// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
export default function AdminPage() {
    const { user, isSuperAdmin } = useAuth();
    const [activeTab, setActiveTab] = useState('users');

    const tabs = [
        { key: 'users', label: 'Users', icon: Users },
        ...(isSuperAdmin ? [{ key: 'permissions', label: 'Permissions', icon: Shield }] : []),
        { key: 'password', label: 'My Password', icon: Key },
    ];

    return (
        <div className="max-w-6xl mx-auto px-6 py-8">
            {/* Header */}
            <div className="flex items-center gap-4 mb-8">
                <div className="w-12 h-12 rounded-2xl flex items-center justify-center shrink-0" style={{
                    background: '#0f6cbd',
                    boxShadow: '0 1px 4px rgba(0,0,0,0.12)',
                }}>
                    <Shield className="w-6 h-6 text-white" />
                </div>
                <div>
                    <h1 className="text-2xl font-extrabold text-slate-900">Admin Panel</h1>
                    <p className="text-sm text-slate-500">Manage users, roles, and permissions</p>
                </div>
            </div>

            {/* Tabs — neumorphic concave container */}
            <div className="flex flex-wrap gap-2 mb-8 p-1.5 rounded-2xl" style={{
                background: 'var(--neu-bg)',
                boxShadow: 'inset 2px 2px 5px rgba(163,177,198,0.45), inset -2px -2px 5px rgba(255,255,255,0.6)',
            }}>
                {tabs.map(tab => {
                    const Icon = tab.icon;
                    return (
                        <button
                            key={tab.key}
                            onClick={() => setActiveTab(tab.key)}
                            className={`inline-flex items-center gap-2 px-5 py-2.5 rounded-xl text-sm font-semibold transition-all duration-200 ${
                                activeTab === tab.key
                                    ? 'text-white'
                                    : 'text-slate-500 hover:text-slate-700'
                            }`}
                            style={activeTab === tab.key ? {
                                background: '#0f6cbd',
                                boxShadow: '0 1px 4px rgba(0,0,0,0.12)',
                            } : {}}
                        >
                            <Icon className="w-4 h-4" />{tab.label}
                        </button>
                    );
                })}
            </div>

            {/* Tab Content — neumorphic raised card */}
            <div className="rounded-3xl p-6 md:p-8" style={{
                background: 'var(--neu-bg)',
                boxShadow: '6px 6px 14px 0 rgba(163,177,198,0.6), -6px -6px 14px 0 rgba(255,255,255,0.8)',
            }}>
                {activeTab === 'users' && <UsersTab currentUser={user} />}
                {activeTab === 'permissions' && isSuperAdmin && <PermissionsTab />}
                {activeTab === 'password' && <ChangePasswordTab />}
            </div>
        </div>
    );
}
